import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, Minus, Send, RotateCcw, ChevronDown, ChevronUp, Package,
  Loader2, Info, History, ShoppingCart, Eye, Download, CheckCircle,
  Clock, XCircle, Edit3, FileText, AlertTriangle
} from 'lucide-react';
import LogoLoader from '../components/LogoLoader';
import BottomNavigation from '../components/BottomNavigation';
import FOSWatermark from '../components/FOSWatermark';
import GuidedTour from '../components/GuidedTour';
import { TOUR_DEFINITIONS } from '../services/tourService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RawMaterialOrder = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('order');
  const [rawMaterials, setRawMaterials] = useState([]);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [productQuantities, setProductQuantities] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [additionalRequirements, setAdditionalRequirements] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [forceTour, setForceTour] = useState(searchParams.get('tour') === 'true');
  
  // Cooldown state
  const [cooldownInfo, setCooldownInfo] = useState(null);
  const [checkingCooldown, setCheckingCooldown] = useState(false);

  const getAuthHeaders = () => {
    const token = user?.token || localStorage.getItem('authToken');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  useEffect(() => {
    if (!user) { toast.error('Please login first'); navigate('/'); return; }
    fetchRawMaterials();
    checkCooldown();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'history' && user) fetchPreviousOrders();
  }, [activeTab, user]);

  const checkCooldown = async () => {
    if (!user?.franchise?.id) return;
    setCheckingCooldown(true);
    try {
      const res = await axios.get(`${API}/raw-material-orders/check-cooldown/${user.franchise.id}`, getAuthHeaders());
      setCooldownInfo(res.data);
    } catch { setCooldownInfo(null); }
    finally { setCheckingCooldown(false); }
  };

  const fetchRawMaterials = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/raw-materials`, { params: { franchise_id: user.franchise.id }, ...getAuthHeaders() });
      setRawMaterials(res.data.materials || res.data || []);
    } catch (error) {
      console.error('Error fetching raw materials:', error);
      toast.error('Failed to load raw materials');
    } finally { setLoading(false); }
  };

  const fetchPreviousOrders = async () => {
    try {
      setLoadingOrders(true);
      const res = await axios.get(`${API}/raw-material-orders`, { params: { franchise_id: user.franchise.id, limit: 50 }, ...getAuthHeaders() });
      setPreviousOrders(res.data.orders || []);
    } catch (error) {
      console.error('Error fetching previous orders:', error);
      toast.error('Failed to load previous orders');
    } finally { setLoadingOrders(false); }
  };

  const toggleCategory = (cat) => setCollapsedCategories(p => ({ ...p, [cat]: !p[cat] }));

  const groupMaterialsByCategory = () => {
    const grouped = {};
    rawMaterials.forEach(m => { const c = m.category || 'Uncategorized'; if (!grouped[c]) grouped[c] = []; grouped[c].push(m); });
    return grouped;
  };

  const increaseQuantity = (id) => setProductQuantities(p => ({ ...p, [id]: (p[id] || 0) + 1 }));
  const decreaseQuantity = (id) => setProductQuantities(p => { const c = p[id] || 0; if (c <= 0) return p; return { ...p, [id]: c - 1 }; });
  const getQuantity = (id) => productQuantities[id] || 0;
  const clearAll = () => { setProductQuantities({}); setAdditionalRequirements(''); toast.info('All quantities reset'); };
  const calculateProductTotal = (m) => getQuantity(m.material_id) * (m.price || 0);
  const calculateGrandTotal = () => rawMaterials.reduce((t, m) => t + calculateProductTotal(m), 0);

  const downloadPDF = (base64Data, orderId) => {
    const bytes = atob(base64Data);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a'); link.href = url; link.download = `Order_${orderId}.pdf`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
  };

  const handleDownloadOrderPdf = async (orderId, version = null) => {
    try {
      setDownloadingPdf(true);
      const url = version ? `${API}/raw-material-orders/${orderId}/pdf/${version}` : `${API}/raw-material-orders/${orderId}/pdf`;
      const res = await axios.get(url, getAuthHeaders());
      if (res.data.success && res.data.pdf_base64) { downloadPDF(res.data.pdf_base64, `${orderId}${version ? `_v${version}` : ''}`); toast.success('PDF downloaded'); }
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloadingPdf(false); }
  };

  const handleViewOrder = async (order) => {
    // Fetch full order details for logs/versions
    try {
      const res = await axios.get(`${API}/raw-material-orders/${order.order_id}`, getAuthHeaders());
      setSelectedOrder(res.data);
    } catch { setSelectedOrder(order); }
    setIsOrderModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login first'); navigate('/'); return; }
    
    const orderItems = [];
    rawMaterials.forEach(m => {
      const q = getQuantity(m.material_id);
      if (q > 0) orderItems.push({ item_name: m.name, size_or_quantity: m.unit || 'unit', quantity: q, price_per_unit: m.price || 0, total_price: q * (m.price || 0) });
    });
    if (orderItems.length === 0) { toast.error('Please add at least one item'); return; }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/raw-material-orders`, {
        franchise_id: user.franchise.id, franchise_name: user.franchise.name,
        location: user.franchise.location || user.franchise.city || '',
        contact_number: user.franchise.contact_number || '',
        ordered_items: orderItems, additional_requirements: additionalRequirements.trim()
      });
      if (res.data.success) {
        if (res.data.pdf_base64) downloadPDF(res.data.pdf_base64, res.data.order_id);
        toast.success(`Order ${res.data.order_id} placed successfully!`, { duration: 5000 });
        setProductQuantities({}); setAdditionalRequirements('');
        setActiveTab('history'); fetchPreviousOrders(); checkCooldown();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to submit order';
      toast.error(detail, { duration: 8000 });
    } finally { setSubmitting(false); }
  };

  const formatDate = (ds) => {
    if (!ds) return 'N/A';
    try { return new Date(ds).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ds; }
  };

  const getOrderStatusBadge = (status) => {
    const configs = {
      'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      'Accepted': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      'Rejected': { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle },
      'Altered': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Edit3 }
    };
    const config = configs[status] || configs['Pending'];
    const Icon = config.icon;
    return (<span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}><Icon className="h-3 w-3" />{status || 'Pending'}</span>);
  };

  const getStatusBadge = (status, type) => {
    const configs = {
      payment: { 'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock }, 'Paid': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle }, 'Partially Paid': { bg: 'bg-orange-100', text: 'text-orange-700', icon: Clock } },
      delivery: { 'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock }, 'Delivered': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle }, 'In Transit': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Package } }
    };
    const config = configs[type]?.[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock };
    const Icon = config.icon;
    return (<span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}><Icon className="h-3 w-3" />{status}</span>);
  };

  // Timeline component
  const OrderTimeline = ({ logs }) => {
    if (!logs || logs.length === 0) return <p className="text-sm text-muted-foreground">No activity logs yet</p>;
    const actionColors = { CREATED: 'bg-blue-500', ACCEPTED: 'bg-green-500', REJECTED: 'bg-red-500', ALTERED: 'bg-amber-500' };
    const actionIcons = { CREATED: Package, ACCEPTED: CheckCircle, REJECTED: XCircle, ALTERED: Edit3 };
    return (
      <div className="space-y-3">
        {logs.map((log, idx) => {
          const Icon = actionIcons[log.action] || Clock;
          const color = actionColors[log.action] || 'bg-gray-500';
          return (
            <div key={idx} className="flex gap-3">
              <div className="flex flex-col items-center">
                <div className={`w-7 h-7 rounded-full ${color} flex items-center justify-center flex-shrink-0`}><Icon className="h-3.5 w-3.5 text-white" /></div>
                {idx < logs.length - 1 && <div className="w-0.5 flex-1 bg-border mt-1" />}
              </div>
              <div className="flex-1 pb-3">
                <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-sm">{log.action}</span><span className="text-xs text-muted-foreground">{formatDate(log.timestamp)}</span></div>
                <p className="text-xs text-secondary">by {log.performed_by}</p>
                {log.metadata?.reason && <p className="text-xs mt-1 p-2 bg-red-50 text-red-700 rounded">Reason: {log.metadata.reason}</p>}
                {log.metadata?.changes && log.metadata.changes.length > 0 && (
                  <div className="mt-1 p-2 bg-amber-50 rounded text-xs">{log.metadata.changes.map((c, i) => <p key={i} className="text-amber-800">{c}</p>)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) return (<div className="min-h-screen bg-background flex items-center justify-center"><LogoLoader size="lg" text="Loading Raw Materials..." /></div>);

  const canPlaceOrder = !cooldownInfo || cooldownInfo.can_order;

  return (
    <div className="min-h-screen bg-background pb-20">
      <GuidedTour tourId="rawMaterialOrder" steps={TOUR_DEFINITIONS.rawMaterialOrder?.steps || []} forceRun={forceTour} onComplete={() => { setForceTour(false); toast.success('Raw Material Order tour completed!'); }} />
      
      {submitting && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6">
            <div className="relative w-28 h-28 sm:w-32 sm:h-32"><img src="/fos-logo.png" alt="FOS Logo" className="w-20 h-20 sm:w-24 sm:h-24 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 object-contain animate-pulse" /><div className="w-32 h-32 border-4 border-t-white border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin" /></div>
            <p className="text-white text-xl font-semibold">Submitting your order...</p>
            <p className="text-white/70 text-sm">Please wait, do not close this page</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div data-testid="raw-material-header" className="border-b border-border bg-surface shadow-sm pt-2">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Package className="h-8 w-8 text-primary" />
            <div><h1 className="text-xl font-bold text-primary">Raw Material Order</h1><p className="text-sm text-secondary">{user?.franchise?.name}</p></div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <div className="flex gap-2 p-1 bg-muted rounded-lg">
          <button onClick={() => setActiveTab('order')} className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md font-medium transition-colors ${activeTab === 'order' ? 'bg-primary text-white shadow' : 'text-secondary hover:bg-muted-foreground/10'}`}>
            <ShoppingCart className="h-4 w-4" />Place Order
          </button>
          <button onClick={() => setActiveTab('history')} className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md font-medium transition-colors ${activeTab === 'history' ? 'bg-primary text-white shadow' : 'text-secondary hover:bg-muted-foreground/10'}`}>
            <History className="h-4 w-4" />My Orders
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Place Order Tab */}
        {activeTab === 'order' && (
          <>
            {/* 24-hour Cooldown Warning */}
            {!canPlaceOrder && cooldownInfo && (
              <Card data-testid="cooldown-warning" className="mb-6 border-amber-300 bg-amber-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-800">Order Cooldown Active</p>
                      <p className="text-sm text-amber-700 mt-1">
                        You can place your next raw material order after <strong>{cooldownInfo.hours_left}h {cooldownInfo.minutes_left}m</strong>.
                      </p>
                      {cooldownInfo.next_eligible_time && (
                        <p className="text-xs text-amber-600 mt-1">Next eligible: {formatDate(cooldownInfo.next_eligible_time)}</p>
                      )}
                      <p className="text-xs text-amber-600 mt-1">Last order: {cooldownInfo.last_order_id}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Franchise Info Card */}
            <Card className="mb-6 bg-primary text-white">
              <CardContent className="p-4">
                <p className="text-sm text-white/80 mb-1">Ordering as:</p>
                <p className="font-bold text-lg">{user?.franchise?.name}</p>
                <p className="text-sm text-white/90">{user?.franchise?.location || user?.franchise?.city}</p>
                <p className="text-sm text-white/80">{user?.franchise?.contact_number}</p>
              </CardContent>
            </Card>

            <form onSubmit={handleSubmit}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-primary">Select Products</h2>
                <Button type="button" variant="outline" size="sm" onClick={clearAll} className="rounded-full"><RotateCcw className="h-4 w-4 mr-2" />Clear All</Button>
              </div>

              <div data-testid="raw-material-items" className="space-y-4 mb-6">
                {Object.entries(groupMaterialsByCategory()).map(([category, materials]) => (
                  <Card key={category} className="overflow-hidden">
                    <div className="flex justify-between items-center p-4 bg-primary text-white cursor-pointer" onClick={() => toggleCategory(category)}>
                      <h3 className="font-bold">{category}</h3>
                      {collapsedCategories[category] ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                    </div>
                    {!collapsedCategories[category] && (
                      <CardContent className="p-3 space-y-3">
                        {materials.map((material) => {
                          const quantity = getQuantity(material.material_id);
                          const total = calculateProductTotal(material);
                          return (
                            <div key={material.material_id} className="flex flex-wrap justify-between items-center p-3 bg-muted rounded-xl gap-3">
                              <div className="flex-1 min-w-[150px]">
                                <p className="font-bold text-primary">{material.name}</p>
                                <p className="text-xs text-muted-foreground">Rs.{(material.price || 0).toFixed(2)} / {material.unit || 'unit'}</p>
                                {material.description && <p className="text-xs text-secondary mt-1 flex items-start gap-1"><Info className="h-3 w-3 mt-0.5 flex-shrink-0" /><span>{material.description}</span></p>}
                              </div>
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm">
                                  <Button type="button" size="icon" className="h-10 w-10 rounded-md bg-primary hover:bg-primary/90" onClick={() => decreaseQuantity(material.material_id)} disabled={quantity === 0}><Minus className="h-4 w-4" /></Button>
                                  <span className="w-10 text-center font-bold text-primary">{quantity}</span>
                                  <Button type="button" size="icon" className="h-10 w-10 rounded-md bg-primary hover:bg-primary/90" onClick={() => increaseQuantity(material.material_id)}><Plus className="h-4 w-4" /></Button>
                                </div>
                                <div className="text-right min-w-[80px]"><p className="text-xs text-secondary">Total</p><p className="font-bold text-primary">Rs.{total.toFixed(2)}</p></div>
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    )}
                  </Card>
                ))}
                {rawMaterials.length === 0 && (
                  <Card><CardContent className="p-8 text-center"><Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground font-medium">No raw materials available</p><p className="text-sm text-muted-foreground mt-1">Contact admin to get raw materials assigned to your franchise</p></CardContent></Card>
                )}
              </div>

              <Card data-testid="raw-material-cart" className="mb-6 bg-primary text-white">
                <CardContent className="p-4 flex justify-between items-center"><span className="text-lg font-semibold">Order Total:</span><span className="text-3xl font-bold">Rs.{calculateGrandTotal().toFixed(2)}</span></CardContent>
              </Card>

              <Card className="mb-6 border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold text-primary mb-1">Additional Requirements (Optional)</h3>
                  <p className="text-sm text-muted-foreground mb-3">Request any items not listed above or special requirements</p>
                  <Textarea value={additionalRequirements} onChange={(e) => setAdditionalRequirements(e.target.value)} placeholder="E.g., Need extra packaging, specific delivery time, etc." rows={3} className="bg-white" />
                </CardContent>
              </Card>

              <Button type="submit" disabled={submitting || calculateGrandTotal() === 0 || !canPlaceOrder} className="w-full h-14 text-lg font-bold rounded-full bg-primary hover:bg-primary/90">
                {submitting ? (<><Loader2 className="h-5 w-5 mr-2 animate-spin" />Processing...</>) : !canPlaceOrder ? (<><Clock className="h-5 w-5 mr-2" />Cooldown Active</>) : (<><Send className="h-5 w-5 mr-2" />Submit Order</>)}
              </Button>
            </form>
          </>
        )}

        {/* Order History Tab */}
        {activeTab === 'history' && (
          <div data-testid="raw-material-history" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-primary">My Previous Orders</h2>
              <Button variant="outline" size="sm" onClick={fetchPreviousOrders} className="rounded-full"><RotateCcw className="h-4 w-4 mr-2" />Refresh</Button>
            </div>

            {loadingOrders ? (
              <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" /><p className="mt-2 text-secondary">Loading orders...</p></div>
            ) : previousOrders.length === 0 ? (
              <Card><CardContent className="p-8 text-center"><History className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><p className="text-muted-foreground font-medium">No previous orders</p><Button className="mt-4" onClick={() => setActiveTab('order')}>Place Your First Order</Button></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {previousOrders.map((order) => (
                  <Card key={order.order_id} className="hover:shadow-md transition-shadow" data-testid={`franchise-rm-order-${order.order_id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-bold text-primary">{order.order_id}</p>
                              {getOrderStatusBadge(order.status)}
                            </div>
                            <p className="text-sm text-muted-foreground">{formatDate(order.timestamp)}</p>
                          </div>
                          <p className="font-bold text-lg text-primary">Rs.{(order.total_amount || 0).toLocaleString()}</p>
                        </div>
                        
                        {order.rejection_reason && (
                          <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            <span className="font-semibold">Rejected:</span> {order.rejection_reason}
                          </div>
                        )}
                        
                        <div className="flex gap-2 flex-wrap">
                          {getStatusBadge(order.payment_status || 'Pending', 'payment')}
                          {getStatusBadge(order.delivery_status || 'Pending', 'delivery')}
                        </div>
                        
                        <p className="text-sm text-secondary">{order.ordered_items?.length || 0} item(s){order.alter_count > 0 ? ` (Altered ${order.alter_count}x)` : ''}</p>
                        
                        <div className="flex gap-2 pt-2 border-t">
                          <Button variant="outline" size="sm" className="flex-1" onClick={() => handleViewOrder(order)}><Eye className="h-4 w-4 mr-2" />View Details</Button>
                          <Button variant="outline" size="sm" className="flex-1" onClick={() => handleDownloadOrderPdf(order.order_id)} disabled={downloadingPdf}>
                            {downloadingPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}Download PDF
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <Dialog open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary">Order Details</DialogTitle>
            <DialogDescription>View your raw material order details</DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-muted-foreground">Order ID</p><p className="font-bold text-primary text-sm">{selectedOrder.order_id}</p></div>
                <div><p className="text-xs text-muted-foreground">Status</p>{getOrderStatusBadge(selectedOrder.status)}</div>
                <div><p className="text-xs text-muted-foreground">Date & Time</p><p className="font-medium text-sm">{formatDate(selectedOrder.timestamp)}</p></div>
                <div><p className="text-xs text-muted-foreground">Total</p><p className="font-bold text-primary">Rs.{(selectedOrder.total_amount || 0).toFixed(2)}</p></div>
              </div>

              {/* Rejection Reason */}
              {selectedOrder.rejection_reason && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-semibold text-red-700 flex items-center gap-1"><XCircle className="h-4 w-4" />Rejection Reason</p>
                  <p className="text-sm text-red-600 mt-1">{selectedOrder.rejection_reason}</p>
                </div>
              )}

              {/* Payment/Delivery Status */}
              <div className="flex gap-2">
                <div><p className="text-xs text-muted-foreground mb-1">Payment</p>{getStatusBadge(selectedOrder.payment_status || 'Pending', 'payment')}</div>
                <div><p className="text-xs text-muted-foreground mb-1">Delivery</p>{getStatusBadge(selectedOrder.delivery_status || 'Pending', 'delivery')}</div>
              </div>

              {/* Current Items */}
              <div>
                <h4 className="font-bold text-primary mb-2">
                  {selectedOrder.status === 'Altered' ? 'Updated Order Items' : 'Ordered Items'}
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted"><tr><th className="text-left p-2 font-medium">Item</th><th className="text-right p-2 font-medium">Qty</th><th className="text-right p-2 font-medium">Price</th><th className="text-right p-2 font-medium">Total</th></tr></thead>
                    <tbody className="divide-y">
                      {selectedOrder.ordered_items?.map((item, idx) => (
                        <tr key={idx}><td className="p-2"><p className="font-medium">{item.item_name}</p><p className="text-xs text-muted-foreground">{item.size_or_quantity}</p></td><td className="p-2 text-right">{item.quantity}</td><td className="p-2 text-right">Rs.{(item.price_per_unit || 0).toFixed(2)}</td><td className="p-2 text-right font-medium">Rs.{(item.total_price || 0).toFixed(2)}</td></tr>
                      ))}
                    </tbody>
                    <tfoot><tr className="bg-primary/5 font-bold"><td colSpan={3} className="p-2 text-right">Grand Total:</td><td className="p-2 text-right text-primary">Rs.{(selectedOrder.total_amount || 0).toFixed(2)}</td></tr></tfoot>
                  </table>
                </div>
              </div>

              {/* Original Order (if altered) */}
              {selectedOrder.original_order_snapshot && (
                <div>
                  <h4 className="font-bold text-amber-700 mb-2 flex items-center gap-1"><Edit3 className="h-4 w-4" />Original Order (Before Alteration)</h4>
                  <div className="border border-amber-200 rounded-lg overflow-hidden bg-amber-50/50">
                    <table className="w-full text-sm">
                      <thead className="bg-amber-100"><tr><th className="text-left p-2 font-medium text-amber-800">Item</th><th className="text-right p-2 font-medium text-amber-800">Qty</th><th className="text-right p-2 font-medium text-amber-800">Total</th></tr></thead>
                      <tbody className="divide-y divide-amber-200">
                        {selectedOrder.original_order_snapshot.ordered_items?.map((item, idx) => (
                          <tr key={idx}><td className="p-2">{item.item_name}</td><td className="p-2 text-right">{item.quantity}</td><td className="p-2 text-right">Rs.{(item.total_price || 0).toFixed(2)}</td></tr>
                        ))}
                      </tbody>
                      <tfoot><tr className="bg-amber-100 font-bold"><td colSpan={2} className="p-2 text-right text-amber-800">Original Total:</td><td className="p-2 text-right text-amber-800">Rs.{(selectedOrder.original_order_snapshot.total_amount || 0).toFixed(2)}</td></tr></tfoot>
                    </table>
                  </div>
                </div>
              )}

              {selectedOrder.additional_requirements && (
                <div><h4 className="font-bold text-primary mb-2">Additional Requirements</h4><p className="text-sm text-secondary p-3 bg-amber-50 rounded-lg border border-amber-200">{selectedOrder.additional_requirements}</p></div>
              )}

              {/* PDF Downloads */}
              <div>
                <h4 className="font-bold text-primary mb-2">Downloads</h4>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="flex-1" onClick={() => handleDownloadOrderPdf(selectedOrder.order_id)} disabled={downloadingPdf}>
                    {downloadingPdf ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Latest PDF
                  </Button>
                  {(selectedOrder.versions || []).filter(v => v.version < (selectedOrder.current_version || 1)).map(v => (
                    <Button key={v.version} size="sm" variant="outline" onClick={() => handleDownloadOrderPdf(selectedOrder.order_id, v.version)} disabled={downloadingPdf}>
                      <FileText className="h-4 w-4 mr-1" />v{v.version} (Original)
                    </Button>
                  ))}
                </div>
              </div>

              {/* Activity Timeline */}
              {selectedOrder.order_logs && selectedOrder.order_logs.length > 0 && (
                <div>
                  <h4 className="font-bold text-primary mb-2 flex items-center gap-1"><History className="h-4 w-4" />Activity Log</h4>
                  <OrderTimeline logs={selectedOrder.order_logs} />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BottomNavigation isOnline={true} activeTab="order" />
      <FOSWatermark opacity={0.06} size="md" />
    </div>
  );
};

export default RawMaterialOrder;
